<template>
  <metainfo></metainfo>

  <div class="container-xl">
    <div class="page-header d-print-none">
      <div class="row g-2 align-items-center">
        <div class="mt-2">
          <CountCommentModeration :payload="{
            channel_id: [getChannelId()],
            type_visible: 2
          }"/>
          <CountPostModeration :payload="{
            channel_id: getChannelId(),
            type_visible: 1
          }"/>
          <CountPostModeration :payload="{
            channel_id: getChannelId(),
            type_visible: 2
          }"/>
          <template v-if="isChannelSite()">
            <CountPromocodeNew :payload="{}"/>
            <CountPromocodeDelete :payload="{}"/>
          </template>
        </div>
        <div class="col-sm">
          <h2 class="page-title">
            <Title :data="{
              'channel_id': getChannelId(),
              'title_page': params.h1,
              'count_posts': countPosts,
              'page': getPageName()
            }"/>
          </h2>
        </div>
        <div class="col-auto">
          <Actions
              :payload="{
                channel_id: getChannelId(),
                page_name: getCurrentPage(),
              }"
          />
        </div>
      </div>
    </div>
  </div>
  <div class="page-body">
    <div class="container-xl">
      <template v-if="isPageNotes()">
        <div class="mb-2">
          <NotesFilter @onFilter="onFilter" :enable="postFilter.enable" :params="postFilter.params"/>
        </div>
      </template>
      <template v-else>
        <div class="mb-2">
          <PostFilter @onFilter="onFilter" :enable="postFilter.enable" :params="postFilter.params" :payload="{
            channel_id: getChannelId(),
          }"/>
        </div>
      </template>
      <div ref="scrollComponent">
        <PostList :query="query" @onRender="onRender" :clear-result="clearResult" />
      </div>
    </div>
  </div>
</template>

<script>
import Title from "../title/Title"
import Actions from "../actions/Actions"
import {defineComponent, ref, onMounted, onUnmounted, watch, toRaw} from "vue"
import {useRoute} from "vue-router"
import PostFilter from "@/modules/post/components/filter/Filter.vue"
import NotesFilter from "@/modules/post/components/filter/NotesFilter"
import PostList from "@/modules/post/components/list/List"
import Helper from "@/services/Helper"
import {useMeta} from "vue-meta"
import PostStoreCommon from "@/modules/post/pages/posts/services/PostStoreCommon"
import CountCommentModeration from "@/components/count_comment_moderation/Index"
import CountPostModeration from "@/components/count_post_moderation/Index"
import CountPromocodeNew from "@/components/count_promocode_new/Index"
import CountPromocodeDelete from "@/components/count_promocode_delete/Index"

export default defineComponent({
  name: "Posts",
  components: {
    PostList,
    PostFilter,
    NotesFilter,
    Title,
    Actions,
    CountCommentModeration,
    CountPostModeration,
    CountPromocodeNew,
    CountPromocodeDelete,
  },
  props: {
    object: {
      type: Object,
      required: true,
    },
    params: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    useMeta({title: props.params.title})

    const route = useRoute()

    let filterParams = props.object.filterParams()

    let postFilter = ref(getFilter())

    let query = ref({})
    let clearResult = ref(false)
    let offset = ref(0)
    let eofResult = ref(false)
    let countPosts = ref(0)

    watch(() => route.query, () => {
      postFilter.value = getFilter()
    })

    function getFilter() {
      return {
        params: {
          search: filterParams.params.search ?? "",
          search_full_match: filterParams.params.search_full_match ?? false,
          date: filterParams.params.date ?? null,
          users: filterParams.params.users ?? [],
          statuses: {
            published: filterParams.params.statuses.published ?? true,
            scheduled: filterParams.params.statuses.scheduled ?? true,
            removed: filterParams.params.statuses.removed ?? false,
            draft: filterParams.params.statuses.draft ?? false,
          },
          shops: filterParams.params.shops ?? [],
          categories: filterParams.params.categories ?? [],
          type_visibility: filterParams.params.type_visibility ?? null,
          is_moderate: filterParams.params.is_moderate ?? null,
          type_id: filterParams.params.type_id ?? [],
          promocode: filterParams.params.promocode ?? "",
        },
        saveForm: filterParams.saveForm ?? true,
        enable: filterParams.enable ?? true,
      }
    }

    function onRender(payload) {
      eofResult.value = payload["oef_items"] === true
      if (payload["count_items"] > 0) {
        countPosts.value = payload["count_items"];
      }
      clearResult.value = false
      isLoading.value = false
    }

    function onFilter(q) {
      clearResult.value = true
      offset.value = 0
      eofResult.value = false
      countPosts.value = 0

      let typeIds = props.params.type_ids
      let typeId = toRaw(q.type_id)

      // если что-то есть, то
      // переопределяем стандартные type_id на те, которые пришли из фильтра
      if (typeId.length > 0) {
        typeIds = typeId
      }

      q = Helper.mergeObject(q, {
        "limit": 18,
        "offset": offset.value,
        "channel_id": getChannelId(),
        "page": props.params.page,
        "type_ids": typeIds,
      })

      q = props.object.prepareQuery(q)

      query.value = q
    }

    function onLoadMore(offset) {
      if (Helper.isEmptyObject(query.value)) {
        return false
      }

      let q = Helper.clone(query.value)

      query.value = Helper.mergeObject(q, {
        "offset": offset,
      })
    }

    onMounted(() => {
      PostStoreCommon.handle({
        "channel_id": getChannelId(),
        "route_name": "Posts",
      })

      window.addEventListener("scroll", handleScroll)
    })

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll)
    })

    const scrollComponent = ref(null)
    let isLoading = ref(false)

    let scroll = () => {
      isLoading.value = true
      offset.value += 18

      onLoadMore(offset.value)
    }

    const handleScroll = () => {
      if (eofResult.value) return

      let element = scrollComponent.value

      if (!isLoading.value && element.getBoundingClientRect().bottom < window.innerHeight) {
        scroll()
      }
    }

    function getCurrentPage() {
      return props.params.page
    }

    function getChannelId() {
      return props.params.channel_id
    }

    function getPageName() {
      return props.params.page
    }

    function isPageNotes() {
      return getPageName() === "notes" || getPageName() === "notes_not_moderated"
    }

    function isChannelSite() {
      return Helper.inArray([getChannelId()], [1])
    }

    function isChannelTelegram() {
      return Helper.inArray([getChannelId()], [2,3,4,5,6,7,8,9,10,11,12])
    }

    return {
      onFilter,
      query,
      scrollComponent,
      onRender,
      clearResult,
      postFilter,
      getCurrentPage,
      getChannelId,
      countPosts,
      getPageName,
      isPageNotes,
      isChannelSite,
      isChannelTelegram,
    }
  },
})
</script>
